import { useCallback, useEffect, useRef } from 'react';

import DeleteAllCardsModal from '@components/DeleteAllCardsModal/DeleteAllCardsModal';
import DeleteCardModal from '@components/DeleteCardModal/DeleteCardModal';
import StoredCreditCards from '@components/StoredCreditCards/StoredCreditCards';
import useEmergencyState from '@hooks/useEmergencyState';
import { useTracking } from '@hooks/useTracking';
import { useTranslation } from '@hooks/useTranslations';
import CoreProviders, { CoreProvidersProps } from '@providers/CoreProviders';
import CreditCardsMutationProvider, {
  useCreditcardService,
} from '@providers/CreditCardsMutationProvider';
import { useErrorContext } from '@providers/ErrorContextProvider';
import { captureException } from '@services/sentryService';
import { ContainerType, CreditCardData, CustomerType, PaymentMethod } from '@types-internal';
import {
  BraintreeCreditCardFormNewHandle as BraintreeCreditCardFormHandle,
  TokenizeCardResult,
  TokenizeCardResultData,
} from '@types-internal/BraintreeTypes';

export interface PaymentMethodSwitchProps extends CoreProvidersProps {
  containerType: ContainerType;
  customerType: CustomerType;
  paymentMethod: PaymentMethod;
  pspToken: string;
  creditCards: CreditCardData[];
  isFallbackPsp: boolean;
  currency?: string;
  customerId?: string;
  sid?: string;
  siteId?: number;
}

type TokenizeResult = {
  redirect: boolean;
  data?: TokenizeCardResultData;
};

/**
 * UI to display the Braintree payment method.
 */
const BraintreeManageCreditCardsSwitch: React.FC<PaymentMethodSwitchProps> = ({
  containerType,
  customerType,
  isFallbackPsp,
  currency,
  customerId,
  sid,
  siteId,
}) => {
  const { t } = useTranslation();
  const formHandle = useRef<BraintreeCreditCardFormHandle>(null);
  const { error, setError } = useErrorContext();
  const {
    data: { creditCards },
  } = useCreditcardService();
  const track = useTracking({
    sid,
    siteId,
    currency,
    customerId,
    guest: customerType === 'GUEST_CUSTOMER',
  });

  // Callback for shop-checkout
  const braintreeTokenizeCard = useCallback(async (): Promise<TokenizeResult> => {
    if (error && !error?.retryable) {
      throw new Error(error.message);
    } else if (!formHandle.current) {
      return { redirect: true };
    } else {
      let tokenizationResult: TokenizeCardResult;
      try {
        tokenizationResult = await formHandle.current?.tokenizeCard();
      } catch (error: any) {
        track?.('BTformKO');
        setError({
          message: t('checkout.payment.creditcard.error.tokenization.retry'),
          retryable: false,
        });
        throw captureException({ message: 'braintree_tokenization_failed' });
      }
      if (tokenizationResult.success) {
        track?.('BTformOK');
        return { redirect: false, data: tokenizationResult.data };
      } else {
        track?.('BTformKO');
        if (tokenizationResult.errorDisplayable) {
          setError({
            message: t('checkout.payment.creditcard.error.tokenization.retry'),
            retryable: true,
          });
        }
        throw new Error('braintree_tokenization_failed');
      }
    }
  }, [t, error, setError]);

  // For calling from parent page
  useEffect(() => {
    (window as any).Payment = {
      ...((window as any).Payment || {}),
      braintreeTokenizeCard,
    };
    return () => {
      (window as any).Payment.braintreeTokenizeCard = undefined;
    };
  }, [braintreeTokenizeCard]);

  const displayPossible = !error || error?.retryable;
  const isMyAccount = containerType === 'myaccount';

  useEmergencyState(isFallbackPsp, isMyAccount, t, setError);

  return (
    <>
      {displayPossible && (!!creditCards?.length || isMyAccount) && (
        <StoredCreditCards isMyAccount={isMyAccount} isFallbackPsp={isFallbackPsp} />
      )}
      {isMyAccount && (
        <>
          <DeleteAllCardsModal />
          <DeleteCardModal />
        </>
      )}
    </>
  );
};

/**
 * Main component covering the UI for the Braintree payment method, both in checkout and in My Account.
 */
const BraintreeManageCreditCards: React.FC<PaymentMethodSwitchProps> = props => {
  const { env, shopBundle, locale, domain, errorContainerId, csrfToken, paymentMethod } = props;

  return (
    <CoreProviders
      env={env}
      shopBundle={shopBundle}
      locale={locale}
      domain={domain}
      errorContainerId={errorContainerId}
      csrfToken={csrfToken}
      featureName="braintree-credit-card">
      {paymentMethod === 'BK' && (
        <CreditCardsMutationProvider
          pspToken={props.pspToken}
          creditCards={props.creditCards}
          paymentMethod="CREDIT_CARD"
          psp="BRAINTREE">
          <BraintreeManageCreditCardsSwitch {...props} />
        </CreditCardsMutationProvider>
      )}
    </CoreProviders>
  );
};

export default BraintreeManageCreditCards;
